import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Handshake, Copy, Eye, Lock, Send } from 'lucide-react';
import {
  getPartners,
  getPartnerApplications,
  updatePartner,
  invitePartnerPortal,
} from '../services/api';
import { formatCurrency } from '../utils/formatCurrency';
import {
  apiErrorCode,
  formatDate,
  formatPeriod,
  isPlatformAdminError,
  localeFor,
  partnerApplyUrl,
} from '../utils/partnersAdmin';
import PartnerProgramLabel from '../components/partners-admin/PartnerProgramLabel';
import PartnerFormModal from '../components/partners-admin/PartnerFormModal';
import PartnerDetailModal from '../components/partners-admin/PartnerDetailModal';
import PartnerPeriodTools from '../components/partners-admin/PartnerPeriodTools';
import PartnerApplicationsPanel from '../components/partners-admin/PartnerApplicationsPanel';
import './PartnersPage.css';

// Base pública de la landing v2 donde vive el route /con/{handle} (nerdo-web).
const LANDING_BASE = 'https://nerdo.solutions/resto/con';

const INVITE_ERRORS = {
  PORTAL_ALREADY_INVITED: [
    'partners.portalInvite.errors.alreadyInvited',
    'Ese partner ya fue invitado al portal.',
  ],
  PORTAL_USER_EXISTS: [
    'partners.portalInvite.errors.userExists',
    'Ese email ya tiene una cuenta en Resto.',
  ],
  PARTNER_TERMINATED: [
    'partners.portalInvite.errors.terminated',
    'El código está dado de baja: no se puede invitar.',
  ],
};

function PartnersPage() {
  const { t, i18n } = useTranslation();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [formPartner, setFormPartner] = useState(undefined); // undefined = cerrado, null = alta
  const [searchQuery, setSearchQuery] = useState('');
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState('codes');
  const [applications, setApplications] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [appFilter, setAppFilter] = useState('pending');
  const [appsLoading, setAppsLoading] = useState(true);
  const [invitingCode, setInvitingCode] = useState(null);

  const locale = localeFor(i18n?.language);
  const fmt = useMemo(
    () => ({
      money: (value) =>
        value === undefined || value === null || value === '' ? '—' : formatCurrency(Number(value)),
      date: (iso) => formatDate(iso, locale),
      period: (period) => formatPeriod(period, locale),
    }),
    [locale]
  );

  const showFeedback = useCallback((type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 5000);
  }, []);
  const showError = useCallback((message) => showFeedback('error', message), [showFeedback]);

  const loadPartners = useCallback(async () => {
    try {
      const response = await getPartners();
      setPartners(response?.partners || []);
      setLoadError(false);
    } catch (error) {
      if (isPlatformAdminError(error)) {
        setForbidden(true);
      } else {
        console.error('Error loading partners:', error);
        setLoadError(true);
      }
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadApplications = useCallback(async (status) => {
    setAppsLoading(true);
    try {
      const response = await getPartnerApplications(status || undefined);
      setApplications(response?.applications || []);
      if (typeof response?.pendingCount === 'number') setPendingCount(response.pendingCount);
    } catch (error) {
      if (isPlatformAdminError(error)) setForbidden(true);
      else console.error('Error loading partner applications:', error);
      setApplications([]);
    } finally {
      setAppsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  useEffect(() => {
    loadApplications(appFilter);
  }, [loadApplications, appFilter]);

  const handleStatusChange = async (partner, status) => {
    if (
      status === 'terminated' &&
      !window.confirm(
        t(
          'partners.confirmTerminate',
          '¿Dar de baja definitiva este código? No se podrá reactivar el histórico de atribución.'
        )
      )
    ) {
      return;
    }
    try {
      await updatePartner(partner.code, { status });
      await loadPartners();
      showFeedback('success', t('partners.statusUpdated', 'Estado actualizado'));
    } catch (error) {
      console.error('Error updating partner status:', error);
      showError(t('partners.saveError', 'Error al guardar el partner'));
    }
  };

  /** RES-93: invita al portal. Devuelve `{ portalUserId, portalInvitedAt }` o null. */
  const handleInvite = async (partner) => {
    if (
      !window.confirm(
        t(
          'partners.portalInvite.confirm',
          '¿Invitar a {{email}} al portal de partners? Le llega un mail con su contraseña.',
          { email: partner.partnerEmail }
        )
      )
    ) {
      return null;
    }
    setInvitingCode(partner.code);
    try {
      const result = await invitePartnerPortal(partner.code);
      setPartners((prev) =>
        prev.map((p) =>
          p.code === partner.code
            ? { ...p, portalUserId: result?.portalUserId, portalInvitedAt: result?.portalInvitedAt }
            : p
        )
      );
      showFeedback('success', t('partners.portalInvite.success', 'Invitación enviada'));
      return result || null;
    } catch (error) {
      const code = apiErrorCode(error);
      if (error?.response?.status === 409 && INVITE_ERRORS[code]) {
        showError(t(...INVITE_ERRORS[code]));
        if (code === 'PORTAL_ALREADY_INVITED') loadPartners();
      } else {
        console.error('Error inviting partner to portal:', error);
        showError(t('partners.portalInvite.errors.generic', 'No se pudo enviar la invitación'));
      }
      return null;
    } finally {
      setInvitingCode(null);
    }
  };

  const copyText = (text) => {
    if (!navigator.clipboard) return;
    navigator.clipboard
      .writeText(text)
      .then(() => showFeedback('success', t('partners.linkCopied', 'Link copiado')))
      .catch(() => showError(t('partners.linkCopyError', 'No se pudo copiar')));
  };

  const handleSaved = async () => {
    setFormPartner(undefined);
    await loadPartners();
    showFeedback('success', t('partners.saveSuccess', 'Partner guardado'));
  };

  const handleReviewed = async (decision) => {
    await Promise.all([
      loadApplications(appFilter),
      decision === 'approved' ? loadPartners() : null,
    ]);
    showFeedback(
      'success',
      decision === 'approved'
        ? t(
            'partners.applications.approvedSuccess',
            'Solicitud aprobada: se creó el código y se invitó al partner a su portal'
          )
        : t('partners.applications.rejectedSuccess', 'Solicitud rechazada')
    );
  };

  const statusLabel = (status) => t(`partners.status.${status}`, status);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return partners;
    const q = searchQuery.toLowerCase();
    return partners.filter(
      (p) =>
        (p.code && p.code.toLowerCase().includes(q)) ||
        (p.partnerName && p.partnerName.toLowerCase().includes(q)) ||
        (p.tier && p.tier.toLowerCase() === q) ||
        (p.status && p.status.toLowerCase().includes(q)) ||
        (p.program && p.program.toLowerCase() === q)
    );
  }, [partners, searchQuery]);

  if (loading) return <div className="loading">{t('common.loading', 'Cargando…')}</div>;

  if (forbidden) {
    return (
      <div className="partners-page">
        <div className="page-header">
          <h1>{t('partners.title', 'Partners')}</h1>
        </div>
        <div className="card">
          <div className="empty-state" data-testid="partners-forbidden">
            <Lock size={48} />
            <p>{t('partners.access.nerdoOnly', 'Este panel es solo para el equipo de Nerdo.')}</p>
          </div>
        </div>
      </div>
    );
  }

  const applyUrl = partnerApplyUrl(window.location.origin, window.location.pathname);

  return (
    <div className="partners-page">
      <div className="page-header">
        <h1>{t('partners.title', 'Partners')}</h1>
        <button type="button" className="btn btn-primary" onClick={() => setFormPartner(null)}>
          {t('partners.addPartner', 'Nuevo código')}
        </button>
      </div>

      {feedback && (
        <div
          className={`alert partners-feedback alert-${feedback.type === 'error' ? 'error' : 'success'}`}
          role="alert"
          aria-live="polite"
        >
          {feedback.message}
        </div>
      )}

      <div className="partners-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'codes'}
          className={`partners-tab${tab === 'codes' ? ' active' : ''}`}
          data-testid="tab-codes"
          onClick={() => setTab('codes')}
        >
          {t('partners.tabs.codes', 'Códigos')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'applications'}
          className={`partners-tab${tab === 'applications' ? ' active' : ''}`}
          data-testid="tab-applications"
          onClick={() => setTab('applications')}
        >
          {t('partners.tabs.applications', 'Solicitudes')}
          {pendingCount > 0 && (
            <span className="tab-counter" data-testid="pending-count">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {tab === 'applications' ? (
        <PartnerApplicationsPanel
          applications={applications}
          loading={appsLoading}
          filter={appFilter}
          onFilterChange={setAppFilter}
          onReviewed={handleReviewed}
          applyUrl={applyUrl}
          onCopyApplyUrl={() => copyText(applyUrl)}
          fmt={fmt}
        />
      ) : (
        <>
          <div className="filters-bar partners-toolbar">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder={t(
                  'partners.searchPlaceholder',
                  'Buscar por código, nombre, tier o estado…'
                )}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            <PartnerPeriodTools onCalculated={loadPartners} onError={showError} fmt={fmt} />
          </div>

          <div className="card">
            {loadError ? (
              <div className="empty-state" data-testid="partners-load-error">
                <p>{t('partners.loadError', 'No se pudieron cargar los partners')}</p>
                <button type="button" className="btn btn-secondary btn-sm" onClick={loadPartners}>
                  {t('common.retry', 'Reintentar')}
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="empty-state">
                <Handshake size={48} />
                <p>{t('partners.emptyState', 'Todavía no hay códigos de partner')}</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="partners-table" data-testid="partners-table">
                  <thead>
                    <tr>
                      <th>{t('partners.code', 'Código')}</th>
                      <th>{t('partners.name', 'Nombre')}</th>
                      <th>{t('partners.program.label', 'Programa')}</th>
                      <th>{t('partners.tier', 'Tier')}</th>
                      <th>{t('partners.statusLabel', 'Estado')}</th>
                      <th>{t('partners.activeConversions', 'Conversiones')}</th>
                      <th>{t('partners.totalMRR', 'MRR')}</th>
                      <th>{t('partners.commissionAccrued', 'Comisión acum.')}</th>
                      <th>{t('partners.lastConversion', 'Última conversión')}</th>
                      <th>{t('common.actions', 'Acciones')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p) => {
                      const m = p.metrics || {};
                      return (
                        <tr key={p.code} data-code={p.code}>
                          <td data-label={t('partners.code', 'Código')}>
                            <code className="partner-code">{p.code}</code>
                          </td>
                          <td data-label={t('partners.name', 'Nombre')}>
                            <span>{p.partnerName}</span>
                            {p.portalUserId && (
                              <span className="portal-badge portal-badge--inline">
                                {t('partners.portalInvite.active', 'Portal activo')}
                              </span>
                            )}
                          </td>
                          <td data-label={t('partners.program.label', 'Programa')}>
                            <PartnerProgramLabel program={p.program} cmTier={m.cmTier} compact />
                          </td>
                          <td data-label={t('partners.tier', 'Tier')}>
                            <span className="tier-badge">{p.tier}</span>
                          </td>
                          <td data-label={t('partners.statusLabel', 'Estado')}>
                            <span className={`status-badge status-${p.status}`}>
                              {statusLabel(p.status)}
                            </span>
                          </td>
                          <td
                            data-label={t('partners.activeConversions', 'Conversiones')}
                            data-metric="conversions"
                          >
                            {m.activeConversions ?? 0}
                            {m.trials > 0 && (
                              <span className="cell-sub">
                                {t('partners.metrics.trials', '+{{count}} en prueba', {
                                  count: m.trials,
                                })}
                              </span>
                            )}
                          </td>
                          <td data-label={t('partners.totalMRR', 'MRR')} data-metric="mrr">
                            {fmt.money(m.mrr ?? 0)}
                          </td>
                          <td
                            data-label={t('partners.commissionAccrued', 'Comisión acum.')}
                            data-metric="commission"
                          >
                            {fmt.money(m.commissionAccrued ?? 0)}
                          </td>
                          <td
                            data-label={t('partners.lastConversion', 'Última conversión')}
                            data-metric="lastConversion"
                          >
                            {fmt.date(m.lastConversion)}
                          </td>
                          <td
                            data-label={t('common.actions', 'Acciones')}
                            className="partners-actions"
                          >
                            <button
                              type="button"
                              className="btn-icon"
                              title={t('partners.viewDetail', 'Ver detalle')}
                              aria-label={t('partners.viewDetail', 'Ver detalle')}
                              data-testid={`detail-${p.code}`}
                              onClick={() => setDetail(p)}
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              type="button"
                              className="btn-icon"
                              title={t('partners.copyLink', 'Copiar link')}
                              aria-label={t('partners.copyLink', 'Copiar link')}
                              onClick={() => copyText(`${LANDING_BASE}/${p.code}`)}
                            >
                              <Copy size={16} />
                            </button>
                            <button
                              type="button"
                              className="btn-icon"
                              title={t('common.edit', 'Editar')}
                              aria-label={t('common.edit', 'Editar')}
                              data-testid={`edit-${p.code}`}
                              onClick={() => setFormPartner(p)}
                            >
                              ✏️
                            </button>
                            {!p.portalUserId && p.status !== 'terminated' && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                data-testid={`invite-${p.code}`}
                                disabled={invitingCode === p.code}
                                onClick={() => handleInvite(p)}
                              >
                                <Send size={14} />{' '}
                                {t('partners.portalInvite.button', 'Invitar al portal')}
                              </button>
                            )}
                            {p.status === 'active' && (
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => handleStatusChange(p, 'paused')}
                              >
                                {t('partners.pause', 'Pausar')}
                              </button>
                            )}
                            {p.status === 'paused' && (
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => handleStatusChange(p, 'active')}
                              >
                                {t('partners.reactivate', 'Reactivar')}
                              </button>
                            )}
                            {p.status !== 'terminated' && (
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => handleStatusChange(p, 'terminated')}
                              >
                                {t('partners.terminate', 'Dar de baja')}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {formPartner !== undefined && (
        <PartnerFormModal
          partner={formPartner}
          onClose={() => setFormPartner(undefined)}
          onSaved={handleSaved}
        />
      )}

      {detail && (
        <PartnerDetailModal
          partner={detail}
          fmt={fmt}
          landingUrl={`${LANDING_BASE}/${detail.code}`}
          onCopyLink={() => copyText(`${LANDING_BASE}/${detail.code}`)}
          onClose={() => setDetail(null)}
          onInvite={handleInvite}
          onEdit={(partner) => {
            setDetail(null);
            setFormPartner(partner);
          }}
        />
      )}
    </div>
  );
}

export default PartnersPage;
